import type {
  Building,
  PermanentBuildingKind,
  EntityId,
  MatchConnection,
  MatchState,
  NeutralUnit,
  PlayerId,
  PlayerView,
  Projectile,
  Squad,
  Unsubscribe,
} from '@signalclash/shared';
import {
  BUILDINGS,
  FOG_VISIBLE,
  MAX_HUBS_PER_PLAYER,
  SLOT_PICK_RADIUS_PX,
  TICK_MS,
  fogAt,
  isOperational,
  pickSlotAt,
  slotWorldPos,
} from '@signalclash/shared';
import Phaser from 'phaser';

import type { BuildMode } from '../input/BuildMode.js';
import type { InputRouter } from '../input/InputRouter.js';
import type { Selection } from '../input/Selection.js';

import { FogOverlay } from './FogOverlay.js';
import {
  drawChannelRing,
  drawCutLinkRings,
  drawMarkRing,
  drawShieldBubbles,
  drawSignalLinks,
} from './SignalLinkOverlay.js';
import { SpriteLayer } from './SpriteLayer.js';

const SQUAD_RADIUS = 14;
const ASSAULT_RADIUS = 17;
const ENGINEER_HALF = 14;
const ENGINEER_NOTCH = 6;
const SHIELD_RADIUS = 18;
const CORE_SELECTION_RADIUS = 80;
const CORE_HP_BAR_WIDTH = 88;
const CORE_HP_BAR_OFFSET_Y = 108;
const HP_BAR_WIDTH = 46;
const HP_BAR_HEIGHT = 6;
const HP_BAR_OFFSET = 30;
const PROJECTILE_RADIUS = 4;
// V1.5+ — bumped from 200 ms to 380 ms so the player has time to FEEL a
// kill land. Below ~300 ms the death pop reads as "did something happen?"
// rather than "I just won that fight."
const DEATH_FX_DURATION_MS = 380;
const HUB_HALF = 26;
const TURRET_RADIUS = 22;
const MINI_TURRET_RADIUS = 14;
// Visual radius for neutral camp patrol units (drawn as Graphics circles).
const CAMP_PATROL_RADIUS = 12;
// Visual radius for neutral turret barrel overlay (matches TURRET_RADIUS).
const CAMP_TURRET_BARREL_RADIUS = 22;
const SLOT_GHOST_RADIUS = 22;
const BUILDING_HP_BAR_WIDTH = 56;
const BUILDING_HP_BAR_OFFSET_Y = 40;

const COLOR_PLAYER_ZERO = 0x4a96ff;
const COLOR_PLAYER_ONE = 0xd55a3a;
const COLOR_NEUTRAL = 0x5a6470;
const COLOR_OUTLINE = 0x0b0d10;
const COLOR_HEADING = 0xeef3f8;
// V1.5+ — selection rings now match the team color rather than yellow gold.
// The old constant-yellow selection ring was indistinguishable from an
// objective highlight; team-color rings disambiguate. Kept around as a
// fallback for neutral selections (none ship in V1.5 but keeping the value
// preserves the option).
const COLOR_SELECT_RING_LEGACY = 0xf5e76e;
const COLOR_SELECT_RING_P0 = 0x88c8ff;  // brightened blue
const COLOR_SELECT_RING_P1 = 0xffc488;  // brightened orange
// Alias kept so the 4 existing references in this file still compile.
// Slot-ghost build-mode use (drawSlotGhosts) is the only spot where the
// gold reading is correct — yellow gold = "you can build here".
const COLOR_SELECT_RING = COLOR_SELECT_RING_LEGACY;
function selectionRingColor(ownerId: PlayerId | null): number {
  if (ownerId === 0) return COLOR_SELECT_RING_P0;
  if (ownerId === 1) return COLOR_SELECT_RING_P1;
  return COLOR_SELECT_RING_LEGACY;
}
const COLOR_BAR_BG = 0x222831;
const COLOR_HP_FULL = 0x4ad06b;
const COLOR_HP_LOW = 0xe2543c;
const COLOR_PROJECTILE_P0 = 0xa9c8ff;
const COLOR_PROJECTILE_P1 = 0xffc89a;
const COLOR_PROJECTILE_NEUTRAL = 0xc9c2a1;
// V1.8 facelift — warmer "camera-flash" color (was 0xfaf3df, bone cream).
// Combined with the punched alpha curve in drawDeathFx, kills now read as a
// genuine pop rather than a quiet fizzle.
const COLOR_DEATH_FX = 0xfff4c0;
const COLOR_SLOT_GHOST = 0xf5e76e;
const COLOR_SLOT_GHOST_BLOCKED = 0xe2543c;
// Teal for neutral camp units — matches C_TEAL palette constant.
const COLOR_CAMP_TEAL   = 0x00d4aa;

interface DeathFx {
  x: number;
  y: number;
  startedAtMs: number;
}

// Reads sim snapshots, interpolates squad/projectile positions between the previous
// and current tick, and draws everything. Holds NO gameplay state. Death FX is purely
// presentational — when a squad disappears between snapshots we spawn a transient
// sprite anchored at its last-known position; sim doesn't model FX entities.
//
// V0.4 adds: build-slot ghosts (when build mode is active), buildings (under-
// construction faded; operational solid), and turret muzzle facing.
export class Renderer {
  private prev: MatchState | null = null;
  private current: MatchState | null = null;
  private lastSnapshotMs = 0;
  private unsub: Unsubscribe;
  private coresGfx: Phaser.GameObjects.Graphics;
  private slotGhostGfx: Phaser.GameObjects.Graphics;
  private nodesGfx: Phaser.GameObjects.Graphics;
  private buildingsGfx: Phaser.GameObjects.Graphics;
  private squadsGfx: Phaser.GameObjects.Graphics;
  // V1.8 facelift — additive team-color halos drawn under every visible
  // squad. Sits below the sprite body (depth 9.5) and above the squad
  // shadow (9.4) so the squad reads as "lit from within."
  private squadHalosGfx: Phaser.GameObjects.Graphics;
  private projectilesGfx: Phaser.GameObjects.Graphics;
  private fxGfx: Phaser.GameObjects.Graphics;
  // Neutral camp units overlay: barrels, HP bars, patrol silhouettes.
  private campUnitsGfx: Phaser.GameObjects.Graphics;
  // V1.5 signal-link layer; depth 4 so it draws under cores/nodes.
  private signalLinksGfx: Phaser.GameObjects.Graphics;
  private fogOverlay: FogOverlay;
  private deathFx: DeathFx[] = [];
  private readonly buildMode: BuildMode;
  // Mutable so the network-mode `setViewerId` can flip the renderer's
  // viewer slot after the server's `assigned` message lands. Local
  // mode keeps the constructor's value (PLAYER_ZERO).
  private viewerId: PlayerId;
  private readonly input: () => InputRouter | null;
  private readonly spriteLayer: SpriteLayer;

  constructor(
    scene: Phaser.Scene,
    connection: MatchConnection,
    private readonly selection: Selection,
    opts: { buildMode: BuildMode; viewerId: PlayerId; getInput: () => InputRouter | null },
  ) {
    this.buildMode = opts.buildMode;
    this.viewerId = opts.viewerId;
    this.input = opts.getInput;
    this.signalLinksGfx = scene.add.graphics();
    this.signalLinksGfx.setDepth(4);
    this.coresGfx = scene.add.graphics();
    this.coresGfx.setDepth(5);
    this.slotGhostGfx = scene.add.graphics();
    this.slotGhostGfx.setDepth(7);
    this.nodesGfx = scene.add.graphics();
    this.nodesGfx.setDepth(6);
    this.buildingsGfx = scene.add.graphics();
    this.buildingsGfx.setDepth(8);
    this.squadsGfx = scene.add.graphics();
    this.squadsGfx.setDepth(10);
    this.squadHalosGfx = scene.add.graphics();
    this.squadHalosGfx.setDepth(9.45);
    this.squadHalosGfx.setBlendMode(Phaser.BlendModes.ADD);
    this.projectilesGfx = scene.add.graphics();
    this.projectilesGfx.setDepth(12);
    this.fogOverlay = new FogOverlay(scene, this.viewerId);
    this.fxGfx = scene.add.graphics();
    this.fxGfx.setDepth(20);
    this.campUnitsGfx = scene.add.graphics();
    // Depth 10.5 puts neutral-unit overlays (turret barrel, HP bars) above the
    // patrol-drone sprite (9.5) and squadsGfx (10) so HP bars are never hidden
    // by the new sprite bodies, but still under projectiles (12).
    this.campUnitsGfx.setDepth(10.5);
    this.spriteLayer = new SpriteLayer(scene);
    this.unsub = connection.onSnapshot((snap: PlayerView) => {
      this.detectDeaths(this.current, snap);
      this.prev = this.current;
      this.current = snap;
      this.lastSnapshotMs = performance.now();
      this.fogOverlay.observeSnapshot(snap);
    });
  }

  // V2.5+ network mode: the server assigns the slot in `onJoin` and
  // the client receives the assignment over the `assigned` message
  // before the first state pump. The Renderer needs to flip its
  // viewer (used for "is this entity friendly", fog overlay, build
  // panel cursor pre-build, hover tooltip, etc.) AND tell the
  // FogOverlay to re-key onto the new fog grid.
  setViewerId(viewerId: PlayerId): void {
    this.viewerId = viewerId;
    this.fogOverlay.setViewerId(viewerId);
  }

  dispose(): void {
    this.unsub();
    this.spriteLayer.dispose();
    this.coresGfx.destroy();
    this.slotGhostGfx.destroy();
    this.nodesGfx.destroy();
    this.buildingsGfx.destroy();
    this.squadsGfx.destroy();
    this.squadHalosGfx.destroy();
    this.projectilesGfx.destroy();
    this.signalLinksGfx.destroy();
    this.fogOverlay.dispose();
    this.fxGfx.destroy();
    this.campUnitsGfx.destroy();
  }

  getLatest(): MatchState | null {
    return this.current;
  }

  render(nowMs: number): void {
    if (this.current === null) return;
    const alpha = clamp01((nowMs - this.lastSnapshotMs) / TICK_MS);

    drawSignalLinks(this.signalLinksGfx, this.current, nowMs);
    this.spriteLayer.syncGroundEffects(this.current);
    this.spriteLayer.syncCores(this.current, nowMs);
    this.spriteLayer.syncRelays(this.current, nowMs);
    this.spriteLayer.syncBuildings(this.current, nowMs);
    this.spriteLayer.syncSquads(
      this.prev, this.current, alpha, nowMs,
      (squad, x, y) => this.isVisibleToViewer(this.current!, squad.ownerId, x, y),
    );
    this.spriteLayer.syncProjectiles(
      this.prev, this.current, alpha,
      (proj, x, y) => this.isVisibleToViewer(this.current!, proj.ownerId ?? this.viewerId, x, y),
    );
    this.spriteLayer.syncNeutralUnits(
      this.prev, this.current, alpha, nowMs,
      (_u, x, y) => this.isNeutralVisible(this.current!, x, y),
    );
    this.drawCores(this.current);
    this.drawNodes(this.current, nowMs);
    this.drawBuildings(this.current);
    this.drawSlotGhosts(this.current);
    this.drawSquads(this.prev, this.current, alpha);
    this.drawProjectiles(this.prev, this.current, alpha);
    this.drawNeutralUnits(this.current);
    this.fogOverlay.redrawIfDirty();
    // fxGfx is cleared inside drawDeathFx; bubbles draw on top of it.
    this.drawDeathFx(nowMs);
    drawShieldBubbles(this.fxGfx, this.current);
  }

  // Per docs/02_GAME_DESIGN.md §5: enemy squads in non-visible cells are NOT
  // drawn ("no ghost units"). Own squads are always drawn even if their pos lies
  // outside the player's own vision (which can't actually happen in V1 since a
  // squad always provides its own vision, but the predicate is symmetric so V2
  // server-side culling can rely on the same rule).
  private isVisibleToViewer(state: MatchState, ownerId: PlayerId, x: number, y: number): boolean {
    if (ownerId === this.viewerId) return true;
    const grid = state.fog[this.viewerId];
    return fogAt(grid, { x, y }) === FOG_VISIBLE;
  }

  private drawCores(state: MatchState): void {
    const g = this.coresGfx;
    g.clear();
    for (const core of Object.values(state.cores)) {
      // Sprite body is drawn by SpriteLayer (depth 4.5, below this layer at depth 5).
      // This layer draws overlays only: selection ring and HP bar.
      if (this.selection.isCoreSelected(core.id)) {
        g.lineStyle(2, selectionRingColor(core.ownerId), 1);
        g.strokeCircle(core.pos.x, core.pos.y, CORE_SELECTION_RADIUS + 6);
      }
      drawHpBarOnGfx(
        g,
        core.pos.x,
        core.pos.y - CORE_HP_BAR_OFFSET_Y,
        CORE_HP_BAR_WIDTH,
        HP_BAR_HEIGHT + 1,
        core.hp / core.maxHp,
      );
    }
  }

  private drawNodes(state: MatchState, nowMs: number): void {
    const g = this.nodesGfx;
    g.clear();
    const flickerOn = Math.floor(nowMs / 100) % 2 === 0;
    // V1.5+ — circular capture ring around each relay. Replaces the old
    // horizontal bar at the top of the relay sprite. A ring reads
    // instantly at a glance ("how full is it?" → "look at the arc") and
    // is anchored to the relay so the eye doesn't have to track a
    // floating UI element. Background ring is a faint full circle;
    // foreground arc fills clockwise from 12 o'clock by progress %.
    const RING_RADIUS = 38;        // outside the 30-px relay sprite footprint
    const RING_THICKNESS = 5;
    const HALF_PI = Math.PI / 2;
    for (const node of Object.values(state.controlNodes)) {
      // Sprite body is drawn by SpriteLayer (depth 5.5, below this layer at depth 6).
      // This layer draws overlays only: capture progress ring and cut-link rings.
      const progress = node.captureProgress;
      const absP = Math.min(1, Math.abs(progress));
      // Background ring — always visible, very dim, full circle.
      g.lineStyle(RING_THICKNESS, COLOR_OUTLINE, 0.55);
      g.strokeCircle(node.pos.x, node.pos.y, RING_RADIUS);
      // Foreground arc — flickers off on contested ticks so the player
      // reads "fight here" without a separate badge.
      if (!(node.contested && !flickerOn) && absP > 0.001) {
        const arcColor = progress > 0 ? COLOR_PLAYER_ZERO : progress < 0 ? COLOR_PLAYER_ONE : COLOR_NEUTRAL;
        g.lineStyle(RING_THICKNESS, arcColor, 1);
        // Begin at 12 o'clock (= -PI/2); sweep clockwise by absP × 2π.
        g.beginPath();
        g.arc(node.pos.x, node.pos.y, RING_RADIUS, -HALF_PI, -HALF_PI + absP * Math.PI * 2, false);
        g.strokePath();
      }
      // Contested badge — a small bright dot at 12 o'clock during
      // contested ticks (the flicker drops the arc, this stays solid).
      if (node.contested) {
        g.fillStyle(0xfff0a0, flickerOn ? 1 : 0.4);
        g.fillCircle(node.pos.x, node.pos.y - RING_RADIUS, 4);
      }
    }
    // V1.5+ Slice 2 — Cut Link overlay (drawn in SignalLinkOverlay so the
    // helper colocates with the rest of the signal-link FX). Drawn on the
    // nodesGfx layer so the red ring sits above the isolation X.
    drawCutLinkRings(g, state, nowMs);
  }

  private drawBuildings(state: MatchState): void {
    const g = this.buildingsGfx;
    g.clear();
    // V1.5+ forward turret network dependency. Offline mini-turrets render at
    // alpha 0.4 with a slow sin-wave flicker (3 Hz, ±0.05 amplitude) so the
    // player reads "that turret is dead" without any text.
    // Sprite bodies are drawn by SpriteLayer (depth 7.5, below this layer at depth 8).
    // This layer draws overlays only: selection rings, gun barrels, and HP/progress bars.
    for (const b of Object.values(state.buildings)) {
      const op = isOperational(b);
      if (this.selection.isBuildingSelected(b.id)) {
        g.lineStyle(2, selectionRingColor(b.ownerId), 1);
        if (b.kind === 'production_hub') {
          g.strokeRect(b.pos.x - HUB_HALF - 4, b.pos.y - HUB_HALF - 4, (HUB_HALF + 4) * 2, (HUB_HALF + 4) * 2);
        } else if (b.kind === 'turret') {
          g.strokeCircle(b.pos.x, b.pos.y, TURRET_RADIUS + 6);
        } else {
          g.strokeCircle(b.pos.x, b.pos.y, MINI_TURRET_RADIUS + 6);
        }
      }
      if (b.kind !== 'production_hub') {
        const radius = b.kind === 'mini_turret' ? MINI_TURRET_RADIUS : TURRET_RADIUS;
        // V1.8 facelift — replaced the single white-line barrel with a
        // tapered metal barrel + muzzle + heat halo. Reads as a real gun
        // emplacement instead of a HUD-marker tick. Mini turrets get a
        // shorter/thinner version; main turrets get the full treatment.
        drawTurretBarrel(g, b.pos.x, b.pos.y, b.facing, radius, b.kind === 'mini_turret', op);
      }
      // Build progress ring for under-construction buildings; HP bar otherwise.
      if (!op) {
        drawHpBarOnGfx(
          g,
          b.pos.x,
          b.pos.y + BUILDING_HP_BAR_OFFSET_Y,
          BUILDING_HP_BAR_WIDTH,
          HP_BAR_HEIGHT + 1,
          b.buildProgress,
          COLOR_SELECT_RING,
        );
      } else if (b.hp < b.maxHp) {
        drawHpBarOnGfx(
          g,
          b.pos.x,
          b.pos.y - BUILDING_HP_BAR_OFFSET_Y,
          BUILDING_HP_BAR_WIDTH,
          HP_BAR_HEIGHT + 1,
          b.hp / b.maxHp,
        );
      }
    }
  }


  private drawSlotGhosts(state: MatchState): void {
    const g = this.slotGhostGfx;
    g.clear();
    const kind: PermanentBuildingKind | null = this.buildMode.getActive();
    if (kind === null) return;

    // Ghost rendering shows the 6 slots ringing the player's Core; a stronger
    // outline highlights the slot the cursor would pick if clicked now. Slots
    // already occupied are rendered red. The hub-cap and signal constraints
    // disable the BuildPanel button rather than block ghost rendering.
    let coreId: number | null = null;
    let centerX = 0;
    let centerY = 0;
    for (const c of Object.values(state.cores)) {
      if (c.ownerId !== this.viewerId) continue;
      coreId = c.id;
      centerX = c.pos.x;
      centerY = c.pos.y;
    }
    if (coreId === null) return;

    const occupied = new Set<number>();
    for (const b of Object.values(state.buildings)) {
      if (b.kind === 'mini_turret') continue;
      if (b.coreId === coreId) occupied.add(b.slotIndex);
    }
    const def = BUILDINGS[kind];
    const player = state.players[this.viewerId];
    const cantAfford = player.signal < def.cost;
    let hubsOwned = 0;
    if (kind === 'production_hub') {
      for (const b of Object.values(state.buildings)) {
        if (b.ownerId === this.viewerId && b.kind === 'production_hub') hubsOwned += 1;
      }
    }

    const cursor = this.input()?.getWorldCursor() ?? null;
    const hovered =
      cursor !== null
        ? pickSlotAt(state, this.viewerId, cursor.x, cursor.y)
        : null;

    for (const c of Object.values(state.cores)) {
      if (c.ownerId !== this.viewerId) continue;
      for (let i = 0; i < 6; i++) {
        const p = slotWorldPos(c, i);
        const isOccupied = occupied.has(i);
        const isHover = hovered !== null && hovered.coreId === coreId && hovered.slotIndex === i;
        const blocked =
          isOccupied || cantAfford || (kind === 'production_hub' && hubsOwned >= MAX_HUBS_PER_PLAYER);
        const color = blocked ? COLOR_SLOT_GHOST_BLOCKED : COLOR_SLOT_GHOST;
        g.lineStyle(isHover ? 3 : 1.5, color, isHover ? 1 : 0.6);
        g.strokeCircle(p.x, p.y, SLOT_GHOST_RADIUS);
        // Inner footprint preview when hovering over an open slot.
        if (isHover && !blocked) {
          g.fillStyle(color, 0.12);
          g.fillCircle(p.x, p.y, SLOT_GHOST_RADIUS - 4);
        }
      }
    }
    // Draw a faint ring to hint the slot reach radius around the Core.
    g.lineStyle(1, COLOR_SLOT_GHOST, 0.2);
    g.strokeCircle(centerX, centerY, SLOT_PICK_RADIUS_PX + 200);
  }

  private drawSquads(prev: MatchState | null, current: MatchState, alpha: number): void {
    const g = this.squadsGfx;
    const h = this.squadHalosGfx;
    g.clear();
    h.clear();
    for (const squad of Object.values(current.squads)) {
      const prevSquad = prev?.squads[squad.id] ?? squad;
      const x = prevSquad.pos.x + (squad.pos.x - prevSquad.pos.x) * alpha;
      const y = prevSquad.pos.y + (squad.pos.y - prevSquad.pos.y) * alpha;
      // Hide enemy squads outside the viewer's current vision. Own squads are
      // always drawn — they are the player's own units and never hide from them.
      if (!this.isVisibleToViewer(current, squad.ownerId, x, y)) continue;
      const facing = lerpAngle(prevSquad.facing, squad.facing, alpha);
      // V1.8 facelift — additive team-color halo on the floor under the squad.
      // 3 concentric discs approximate a radial-gradient glow on a Graphics
      // layer (no per-squad sprite). Tighter inner ring stays bright; outer
      // ring is a soft spread that reads as "this unit is powered."
      const haloColor = colorForOwner(squad.ownerId);
      const radius = squadFootprintRadius(squad);
      h.fillStyle(haloColor, 0.04);
      h.fillCircle(x, y, radius + 28);
      h.fillStyle(haloColor, 0.09);
      h.fillCircle(x, y, radius + 16);
      h.fillStyle(haloColor, 0.18);
      h.fillCircle(x, y, radius + 6);
      this.drawSquad(g, squad, x, y, facing);
    }
  }

  private drawSquad(
    g: Phaser.GameObjects.Graphics,
    squad: Squad,
    x: number,
    y: number,
    facing: number,
  ): void {
    const radius = squadFootprintRadius(squad);
    const useSprite = this.spriteLayer.squadsReady();
    if (this.selection.isSquadSelected(squad.id)) {
      g.lineStyle(2, selectionRingColor(squad.ownerId), 1);
      g.strokeCircle(x, y, radius + 4);
      // V1.5+ — floor decal under the selected squad: a soft team-colour
      // disc so selection reads even at 0.5× zoom where the 2 px ring
      // can disappear against busy backgrounds.
      g.fillStyle(selectionRingColor(squad.ownerId), 0.18);
      g.fillCircle(x, y, radius + 8);
    }
    const fill = colorForOwner(squad.ownerId);
    if (!useSprite) {
      // Fallback path — sprite atlas not loaded (tests / failure). Original
      // Graphics-only silhouette so the game still renders.
      g.fillStyle(fill, 0.12);
      g.fillCircle(x, y, radius + 10);
      g.fillStyle(fill, 0.22);
      g.fillCircle(x, y, radius + 4);
      drawSquadSilhouette(g, squad, x, y, facing, fill);
    }

    // World-space HP bar above the squad. Hidden when the squad is at full health
    // to reduce visual clutter; reappears the moment damage lands.
    if (squad.hp < squad.maxHp) {
      drawHpBarOnGfx(g, x, y - HP_BAR_OFFSET, HP_BAR_WIDTH, HP_BAR_HEIGHT, squad.hp / squad.maxHp);
    }
    // Mark Target ring: animated red ring while the squad is marked. Drawn for
    // any viewer (the target itself, the marker, and bystanders) — visibility
    // through fog is already handled by the FogSystem mark stamp.
    const current = this.current;
    if (
      current !== null &&
      squad.markedByPlayerId !== null &&
      current.tick < squad.markedUntilTick
    ) {
      drawMarkRing(g, x, y, performance.now());
    }
    // V1.5+ Slice 2 — channel ring around a Recon mid-Cut Link. Yellow so it
    // reads as distinct from the red Mark Target ring. The ring announces the
    // cast to the defender (clear counterplay window per §13: shoot the Recon
    // to interrupt). The squad itself is immobilized + holds fire (sim-driven
    // via Squad.channelingAbility). One ring; pulse only.
    if (squad.channelingAbility !== null) {
      drawChannelRing(g, x, y, performance.now());
    }
  }

  private drawProjectiles(prev: MatchState | null, current: MatchState, alpha: number): void {
    const g = this.projectilesGfx;
    g.clear();
    // When fx atlas is loaded, SpriteLayer handles bullet rendering — skip Graphics dots.
    if (this.spriteLayer.fxReady()) return;
    for (const proj of Object.values(current.projectiles)) {
      const prevProj = prev?.projectiles[proj.id] ?? proj;
      const x = prevProj.pos.x + (proj.pos.x - prevProj.pos.x) * alpha;
      const y = prevProj.pos.y + (proj.pos.y - prevProj.pos.y) * alpha;
      // Enemy projectiles only render in cells the viewer currently sees.
      // Neutral camp projectiles (ownerId=null) render whenever the viewer
      // can see the firing cell — same fog rule, but treated as the viewer's
      // own side for the visibility check so a neutral shot doesn't reveal
      // through fog.
      const visibilityOwner = proj.ownerId ?? this.viewerId;
      if (!this.isVisibleToViewer(current, visibilityOwner, x, y)) continue;
      this.drawProjectile(g, proj, x, y);
    }
  }

  private drawProjectile(
    g: Phaser.GameObjects.Graphics,
    proj: Projectile,
    x: number,
    y: number,
  ): void {
    const color = proj.ownerId === null
      ? COLOR_PROJECTILE_NEUTRAL
      : proj.ownerId === 0
        ? COLOR_PROJECTILE_P0
        : COLOR_PROJECTILE_P1;
    g.fillStyle(color, 1);
    g.fillCircle(x, y, PROJECTILE_RADIUS);
    g.lineStyle(1, COLOR_OUTLINE, 0.7);
    g.strokeCircle(x, y, PROJECTILE_RADIUS);
  }

  private drawDeathFx(nowMs: number): void {
    this.fxGfx.clear();
    const survivors: DeathFx[] = [];
    for (const fx of this.deathFx) {
      const t = (nowMs - fx.startedAtMs) / DEATH_FX_DURATION_MS;
      if (t >= 1) continue;
      survivors.push(fx);
      const alpha = 1 - t;
      // V1.8 facelift — wider expansion (0.6 → 1.0) so the ring travels
      // further, plus a brighter inner pop that decays sharper. Reads as a
      // genuine flash instead of a soft puff.
      const radius = SQUAD_RADIUS * (1 + t * 1.0);
      // Outer ring — 3 px (was 2), holds energy longer.
      this.fxGfx.lineStyle(3, COLOR_DEATH_FX, alpha);
      this.fxGfx.strokeCircle(fx.x, fx.y, radius);
      // Inner hot core — punched up to 0.95 initial alpha (was 0.65).
      this.fxGfx.fillStyle(COLOR_DEATH_FX, alpha * (0.95 - 0.55 * t));
      this.fxGfx.fillCircle(fx.x, fx.y, SQUAD_RADIUS * (1 - t * 0.5));
      // Soft outer halo — adds the "bloom" feel even without GPU bloom.
      this.fxGfx.fillStyle(COLOR_DEATH_FX, alpha * 0.20);
      this.fxGfx.fillCircle(fx.x, fx.y, radius + 6);
    }
    this.deathFx = survivors;
  }

  private detectDeaths(prev: MatchState | null, next: MatchState): void {
    if (prev === null) return;
    for (const id of Object.keys(prev.squads)) {
      const key = id as unknown as EntityId;
      if (next.squads[key] !== undefined) continue;
      const dead: Squad | undefined = prev.squads[key];
      if (dead === undefined) continue;
      // Don't surface deaths the viewer wasn't watching. Own deaths always show.
      if (!this.isVisibleToViewer(next, dead.ownerId, dead.pos.x, dead.pos.y)) continue;
      this.deathFx.push({ x: dead.pos.x, y: dead.pos.y, startedAtMs: performance.now() });
    }
    for (const id of Object.keys(prev.buildings)) {
      const key = id as unknown as EntityId;
      if (next.buildings[key] !== undefined) continue;
      const dead: Building | undefined = prev.buildings[key];
      if (dead === undefined) continue;
      if (!this.isVisibleToViewer(next, dead.ownerId, dead.pos.x, dead.pos.y)) continue;
      this.deathFx.push({ x: dead.pos.x, y: dead.pos.y, startedAtMs: performance.now() });
    }
  }

  // ---------------------------------------------------------------------------
  // Neutral camp units — overlay layer. Sprite bodies (turret + patrol drone)
  // are drawn by SpriteLayer.syncNeutralUnits at depth 7.6 / 9.5; this layer
  // adds the turret's barrel line and HP bars over the top at depth 8.5.
  // All units are fog-culled: neutral units are hostile, so they only render
  // in cells the viewer can currently see (same rule as enemy squads).
  // ---------------------------------------------------------------------------
  private drawNeutralUnits(state: MatchState): void {
    const g = this.campUnitsGfx;
    g.clear();
    for (const u of Object.values(state.neutralUnits) as NeutralUnit[]) {
      if (!this.isVisibleToViewer(state, this.viewerId === 0 ? 1 : 0, u.pos.x, u.pos.y)) {
        // Neutral units are visible when ANY player can see the cell — use
        // viewer's own fog so they only reveal when the viewer is nearby.
        if (!this.isNeutralVisible(state, u.pos.x, u.pos.y)) continue;
      }
      if (u.kind === 'turret') {
        // Turret barrel line — sprite body is drawn by SpriteLayer.
        const tipX = u.pos.x + Math.cos(u.facing) * (CAMP_TURRET_BARREL_RADIUS + 6);
        const tipY = u.pos.y + Math.sin(u.facing) * (CAMP_TURRET_BARREL_RADIUS + 6);
        g.lineStyle(3, COLOR_CAMP_TEAL, 0.9);
        g.lineBetween(u.pos.x, u.pos.y, tipX, tipY);
      }
      // HP bar when damaged (same style as player buildings).
      if (u.hp < u.maxHp) {
        const barY = u.kind === 'turret'
          ? u.pos.y - BUILDING_HP_BAR_OFFSET_Y
          : u.pos.y - CAMP_PATROL_RADIUS - 8;
        drawHpBarOnGfx(g, u.pos.x, barY, BUILDING_HP_BAR_WIDTH, HP_BAR_HEIGHT + 1, u.hp / u.maxHp);
      }
    }
  }

  // Neutral units are drawn whenever the viewer's own fog reveals the cell.
  private isNeutralVisible(state: MatchState, x: number, y: number): boolean {
    const grid = state.fog[this.viewerId];
    return fogAt(grid, { x, y }) === FOG_VISIBLE;
  }
}

// V1.8 facelift — tapered turret barrel with a muzzle ring and a soft heat
// halo. Drawn from the turret's pivot outward along `facing`. Mini turrets
// use a shorter/thinner version; permanent turrets use the full one.
// Drawn entirely on the buildings Graphics layer — purely additive overlay,
// no extra texture allocations.
function drawTurretBarrel(
  g: Phaser.GameObjects.Graphics,
  cx: number, cy: number,
  facing: number,
  radius: number,
  mini: boolean,
  operational: boolean,
): void {
  const opAlpha = operational ? 1 : 0.4;
  const cosF = Math.cos(facing);
  const sinF = Math.sin(facing);
  // Perpendicular unit vector for thickness offsets.
  const perpX = -sinF;
  const perpY = cosF;
  const startInset = mini ? 2 : 4;        // barrel begins inside the dome
  const length     = mini ? 10 : 16;      // total barrel length past the rim
  const halfWidth  = mini ? 1.6 : 2.4;    // half-thickness at the base
  const tipHalfW   = mini ? 1.0 : 1.6;    // narrower at the muzzle = taper
  const muzzleR    = mini ? 1.8 : 2.6;    // outer ring at the tip

  const baseCX = cx + cosF * (radius - startInset);
  const baseCY = cy + sinF * (radius - startInset);
  const tipCX  = cx + cosF * (radius + length);
  const tipCY  = cy + sinF * (radius + length);

  // 1) Tapered barrel polygon — bright metal fill + dark outline.
  const bl = { x: baseCX + perpX * halfWidth,  y: baseCY + perpY * halfWidth };
  const br = { x: baseCX - perpX * halfWidth,  y: baseCY - perpY * halfWidth };
  const tl = { x: tipCX  + perpX * tipHalfW,   y: tipCY  + perpY * tipHalfW };
  const tr = { x: tipCX  - perpX * tipHalfW,   y: tipCY  - perpY * tipHalfW };
  g.fillStyle(0x1a2028, opAlpha);
  g.beginPath();
  g.moveTo(bl.x, bl.y);
  g.lineTo(tl.x, tl.y);
  g.lineTo(tr.x, tr.y);
  g.lineTo(br.x, br.y);
  g.closePath();
  g.fillPath();
  // Bright top edge — sells the "lit from above" 3D feel.
  g.lineStyle(1, 0xeef3f8, 0.45 * opAlpha);
  g.lineBetween(bl.x, bl.y, tl.x, tl.y);
  // Dark bottom edge
  g.lineStyle(1, 0x05080c, 0.85 * opAlpha);
  g.lineBetween(br.x, br.y, tr.x, tr.y);
  // 2) Muzzle ring at the tip — a circular metal collar.
  g.fillStyle(0x2a3340, opAlpha);
  g.fillCircle(tipCX, tipCY, muzzleR);
  g.lineStyle(1, 0x05080c, 0.85 * opAlpha);
  g.strokeCircle(tipCX, tipCY, muzzleR);
  // 3) Muzzle bore — small dark dot at the very tip.
  g.fillStyle(0x05080c, opAlpha);
  g.fillCircle(tipCX, tipCY, muzzleR * 0.45);
  // 4) Heat halo — faint additive-feel orange glow at the muzzle while
  // operational. Achieved with a low-alpha fill on the same Graphics layer
  // (true additive blending isn't available on the building Graphics layer
  // without splitting it; this looks plenty hot at low alpha).
  if (operational) {
    g.fillStyle(0xff9a40, 0.18);
    g.fillCircle(tipCX, tipCY, muzzleR + 2.5);
    g.fillStyle(0xffd070, 0.30);
    g.fillCircle(tipCX, tipCY, muzzleR * 0.7);
  }
}

function drawHpBarOnGfx(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  width: number,
  height: number,
  ratio: number,
  fillOverride?: number,
): void {
  // V1.8 facelift — polished pill-style HP bar. Previous version was a flat
  // rect with a 1-px outline. New version adds: a deep drop-shadow row below
  // the bar (sinks the bar into the floor), a recessed dark background, a
  // bright top highlight strip on the fill (sells "lit lozenge"), and a
  // tiny shaded tip at the right edge of the fill (so the fill end-cap
  // reads as 3D instead of a hard truncation).
  const r = clamp01(ratio);
  const x = cx - width / 2;
  // Drop shadow — 1 px below.
  g.fillStyle(0x000000, 0.45);
  g.fillRect(x - 1, cy + 1, width + 2, height + 1);
  // Recessed dark background — slightly darker than the previous BG.
  g.fillStyle(0x0c1118, 0.92);
  g.fillRect(x, cy, width, height);
  // Inner inset hairline (dark) — sells the recessed feel.
  g.lineStyle(1, 0x000000, 0.45);
  g.strokeRect(x + 0.5, cy + 0.5, width - 1, height - 1);
  // Filled portion.
  const fw = width * r;
  if (fw > 0) {
    const color = fillOverride ?? (r > 0.4 ? COLOR_HP_FULL : COLOR_HP_LOW);
    // Main body — slightly inset from the bar (1 px margin) so the dark
    // background reads as a frame.
    g.fillStyle(color, 1);
    g.fillRect(x + 1, cy + 1, Math.max(0, fw - 2), height - 2);
    // Top highlight strip — bright lozenge feel.
    g.fillStyle(0xffffff, 0.30);
    g.fillRect(x + 1, cy + 1, Math.max(0, fw - 2), Math.max(1, (height - 2) * 0.35));
    // Right-edge dim — fakes a curved end-cap shadow.
    if (fw > 4) {
      g.fillStyle(0x000000, 0.30);
      g.fillRect(x + fw - 2, cy + 1, 1, height - 2);
    }
  }
  // Outer outline — tighter than the previous version.
  g.lineStyle(1, COLOR_OUTLINE, 0.95);
  g.strokeRect(x, cy, width, height);
}

function colorForOwner(owner: PlayerId | null): number {
  if (owner === 0) return COLOR_PLAYER_ZERO;
  if (owner === 1) return COLOR_PLAYER_ONE;
  return COLOR_NEUTRAL;
}

function squadFootprintRadius(squad: Squad): number {
  switch (squad.archetype) {
    case 'assault':
      return ASSAULT_RADIUS;
    case 'engineer':
      return ENGINEER_HALF;
    case 'shield':
      return SHIELD_RADIUS;
    default:
      return SQUAD_RADIUS;
  }
}

// Per-archetype silhouette. Recon stays the original triangle-on-circle; Assault
// keeps its larger circle; Engineer is a notched square; Shield is a chunky
// shield-bearer (rounded square with a frontal bar). Heading triangle still draws
// for combat archetypes; the Engineer notch indicates facing on its own.
function drawSquadSilhouette(
  g: Phaser.GameObjects.Graphics,
  squad: Squad,
  x: number,
  y: number,
  facing: number,
  fill: number,
): void {
  if (squad.archetype === 'engineer') {
    drawEngineerSilhouette(g, x, y, facing, fill);
    return;
  }
  if (squad.archetype === 'shield') {
    drawShieldSilhouette(g, x, y, facing, fill);
    return;
  }
  const radius = squad.archetype === 'assault' ? ASSAULT_RADIUS : SQUAD_RADIUS;
  g.fillStyle(fill, 1);
  g.fillCircle(x, y, radius);
  g.lineStyle(1.5, COLOR_OUTLINE, 1);
  g.strokeCircle(x, y, radius);
  drawHeadingTriangle(g, x, y, facing, radius);
}

function drawEngineerSilhouette(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  facing: number,
  fill: number,
): void {
  // Square footprint with a rectangular notch carved out of the front edge to
  // signal facing. Drawn as: filled square → outline → contrasting notch wedge.
  g.fillStyle(fill, 1);
  g.fillRect(x - ENGINEER_HALF, y - ENGINEER_HALF, ENGINEER_HALF * 2, ENGINEER_HALF * 2);
  g.lineStyle(1.5, COLOR_OUTLINE, 1);
  g.strokeRect(x - ENGINEER_HALF, y - ENGINEER_HALF, ENGINEER_HALF * 2, ENGINEER_HALF * 2);
  const cosF = Math.cos(facing);
  const sinF = Math.sin(facing);
  const tipX = x + cosF * ENGINEER_HALF;
  const tipY = y + sinF * ENGINEER_HALF;
  g.fillStyle(COLOR_HEADING, 1);
  g.beginPath();
  g.moveTo(tipX, tipY);
  g.lineTo(tipX - cosF * ENGINEER_NOTCH + sinF * ENGINEER_NOTCH, tipY - sinF * ENGINEER_NOTCH - cosF * ENGINEER_NOTCH);
  g.lineTo(tipX - cosF * ENGINEER_NOTCH - sinF * ENGINEER_NOTCH, tipY - sinF * ENGINEER_NOTCH + cosF * ENGINEER_NOTCH);
  g.closePath();
  g.fillPath();
}

function drawShieldSilhouette(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  facing: number,
  fill: number,
): void {
  // Chunky disc with a heavy frontal bar — visually reads as a shield-bearer.
  g.fillStyle(fill, 1);
  g.fillCircle(x, y, SHIELD_RADIUS);
  g.lineStyle(2.5, COLOR_OUTLINE, 1);
  g.strokeCircle(x, y, SHIELD_RADIUS);
  const cosF = Math.cos(facing);
  const sinF = Math.sin(facing);
  const barX = x + cosF * (SHIELD_RADIUS - 2);
  const barY = y + sinF * (SHIELD_RADIUS - 2);
  const perpX = -sinF;
  const perpY = cosF;
  g.lineStyle(4, COLOR_HEADING, 1);
  g.lineBetween(
    barX + perpX * SHIELD_RADIUS * 0.55,
    barY + perpY * SHIELD_RADIUS * 0.55,
    barX - perpX * SHIELD_RADIUS * 0.55,
    barY - perpY * SHIELD_RADIUS * 0.55,
  );
}

function drawHeadingTriangle(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  facing: number,
  radius: number,
): void {
  const tipX = x + Math.cos(facing) * (radius - 2);
  const tipY = y + Math.sin(facing) * (radius - 2);
  const baseAngle = facing + Math.PI / 2;
  const baseHalf = radius * 0.45;
  const baseX1 = x + Math.cos(baseAngle) * baseHalf;
  const baseY1 = y + Math.sin(baseAngle) * baseHalf;
  const baseX2 = x - Math.cos(baseAngle) * baseHalf;
  const baseY2 = y - Math.sin(baseAngle) * baseHalf;
  g.fillStyle(COLOR_HEADING, 1);
  g.beginPath();
  g.moveTo(tipX, tipY);
  g.lineTo(baseX1, baseY1);
  g.lineTo(baseX2, baseY2);
  g.closePath();
  g.fillPath();
}

function clamp01(v: number): number {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

function lerpAngle(a: number, b: number, t: number): number {
  let diff = b - a;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return a + diff * t;
}
